import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EducationApi } from '@core/apis/education/education.api';
import { PatientApi } from '@core/apis/patient/patient.api';
import { IPatientSignUpWithPasswordRequest } from '@core/apis/patient/patient.interface';
import { RehabilitationApi } from '@core/apis/rehabilitation/rehabilitation.api';
import { ServiceApi } from '@core/apis/service/service.api';
import { HeaderComponent } from '@core/components/header/header.component';
import { InfoProcessingModalComponent } from '@core/components/info-processing-modal/info-processing-modal.component';
import { RequiredAsteriskDirective } from '@core/directive/required-asterisk.directive';
import { ReusableProgramsMethodsService } from '@core/services/reusable-programs-methods.service';
import { StorageService } from '@core/services/storage.service';
import { PasswordValidator } from '@core/validators/password.validator';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RegistrationSuccessComponent } from './registration-success/registration-success.component';
import { InputPhoneEmailComponent } from '@core/components/input-phone-email/input-phone-email.component';
import { ContactAuthHelper } from '@core/helpers/contact-auth.helper';

@Component({
  selector: 'app-patient-sign-up',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    RouterModule,
    RegistrationSuccessComponent,
    HeaderComponent,
    InfoProcessingModalComponent,
    RequiredAsteriskDirective,
    InputPhoneEmailComponent,
  ],
  templateUrl: './patient-sign-up.component.html',
  styleUrl: './patient-sign-up.component.scss',
})
@UntilDestroy()
export class PatientSignUpComponent extends ReusableProgramsMethodsService {
  public isLoading: boolean = false;
  public form!: FormGroup;
  public passwordChecked: boolean = false;
  public registrationSuccess: boolean = false;
  @ViewChild(InfoProcessingModalComponent)
  modal!: InfoProcessingModalComponent;
  constructor(
    private readonly fb: FormBuilder,
    private readonly patientApi: PatientApi,
    protected readonly storageService: StorageService,
    protected override educationApi: EducationApi,
    protected override rehabilitationApi: RehabilitationApi,
    protected override serviceApi: ServiceApi,
  ) {
    super(storageService, educationApi, rehabilitationApi, serviceApi);
    this.buildForm();
    this.getPrograms();
  }

  public openModal() {
    this.modal.openModal();
  }

  private passwordsMatch(control: AbstractControl) {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('repeatPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private buildForm(): void {
    this.form = this.fb.group(
      {
        contact: [null, [Validators.required, ContactAuthHelper.emailOrPhoneValidator()]],
        firstName: [null, Validators.required],
        password: [null, [Validators.required, PasswordValidator.validate]],
        repeatPassword: [null, Validators.required],
      },
      { validators: [this.passwordsMatch] },
    );
  }

  onSubmit() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const { contact, repeatPassword, ...formValue } = this.form.value;
    const resolvedContact = ContactAuthHelper.resolveContact(contact);
    const patientCreate: IPatientSignUpWithPasswordRequest = {
      ...formValue,
      ...resolvedContact,
      sendSms: false,
    };

    this.patientApi
      .signUpWithPassword(patientCreate)
      .pipe(untilDestroyed(this))
      .subscribe({
        complete: () => {
          this.isLoading = false;
          this.registrationSuccess = true;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }
}
